import { Ref, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { heightInches } from 'v5/core/entity-values/codecs/height.codec';
import { FormField } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import {
  InputGroup,
  InputGroupInput,
  InputGroupSlot
} from 'v5/design-system/InputGroup';
import { cn } from 'v5/platform/dom/cn';
import { useFieldContext } from '../FieldContext';
import { fieldAnchor } from '../unitPath';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';
import { ghostRowClassName } from './listLayout';

const MAX_INCHES = 11;

type HeightParts = { feet: string; inches: string };

function partsOf(total: number | null): HeightParts {
  if (total === null) return { feet: '', inches: '' };
  const { feet, inches } = heightInches.split(total);
  return { feet: String(feet), inches: String(inches) };
}

function parsePart(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function totalOf(parts: HeightParts): number | null {
  return heightInches.join(parsePart(parts.feet), parsePart(parts.inches));
}

/**
 * HEIGHT custom field (legacy `HEIGHT.jsx` / `HeightInput`): feet + inches
 * inputs over a single total-inches value; display mode shows `5' 11"`.
 */
export function HeightField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'height'>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const totalInches: number | null = useWatch({ name: path }) ?? null;

  if (readOnly && totalInches === null) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field }) => (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          {readOnly ? (
            <Input
              readOnly
              variant={ctx.variant}
              size={ctx.size}
              name={field.name}
              ref={field.ref}
              value={heightInches.format(field.value ?? null)}
              onChange={() => undefined}
              aria-label={spec.label}
            />
          ) : (
            <HeightInputs
              label={spec.label}
              name={field.name}
              value={field.value ?? null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        </FieldControlBase>
      )}
    />
  );
}

/**
 * Keeps the two inputs as typed text so clearing one part does not snap the
 * other to `0`; the draft is dropped as soon as the form value changes from
 * outside (reset / cancel).
 */
function HeightInputs({
  label,
  name,
  value,
  onChange,
  onBlur,
  inputRef
}: {
  label: string;
  name: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
  inputRef: Ref<HTMLInputElement>;
}) {
  const ctx = useFieldContext();
  const [draft, setDraft] = useState<{
    parts: HeightParts;
    total: number | null;
  } | null>(null);
  const parts =
    draft !== null && draft.total === value ? draft.parts : partsOf(value);

  const update = (next: HeightParts) => {
    const feet = parsePart(next.feet);
    const inches = parsePart(next.inches);
    // Legacy `HeightValue` bounds: whole numbers, never negative, inches
    // below a foot. A fraction is refused rather than shown and dropped.
    if (feet !== null && (!Number.isInteger(feet) || feet < 0)) return;
    if (
      inches !== null &&
      (!Number.isInteger(inches) || inches < 0 || inches > MAX_INCHES)
    ) {
      return;
    }
    const total = totalOf(next);
    setDraft({ parts: next, total });
    onChange(total);
  };

  const groupClassName = cn('flex-1', ctx.ghost && ghostRowClassName);

  return (
    <div className="flex flex-row gap-2">
      <InputGroup
        size={ctx.size}
        variant={ctx.variant}
        className={groupClassName}
      >
        <InputGroupInput
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          autoComplete="off"
          name={`${name}.feet`}
          {...fieldAnchor(`${name}.feet`)}
          ref={inputRef}
          placeholder="Enter feet"
          aria-label={`${label} feet`}
          value={parts.feet}
          onChange={event => update({ ...parts, feet: event.target.value })}
          onBlur={onBlur}
        />
        <InputGroupSlot className="text-fg-muted" aria-hidden="true">
          ft
        </InputGroupSlot>
      </InputGroup>
      <InputGroup
        size={ctx.size}
        variant={ctx.variant}
        className={groupClassName}
      >
        <InputGroupInput
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_INCHES}
          step={1}
          autoComplete="off"
          name={`${name}.inches`}
          {...fieldAnchor(`${name}.inches`)}
          placeholder="Enter inches"
          aria-label={`${label} inches`}
          value={parts.inches}
          onChange={event => update({ ...parts, inches: event.target.value })}
          onBlur={onBlur}
        />
        <InputGroupSlot className="text-fg-muted" aria-hidden="true">
          in
        </InputGroupSlot>
      </InputGroup>
    </div>
  );
}
